import * as THREE from 'three';
import OBJECT_NAMES from 'components/customer/RoomPlanner/constants/ObjectNameMeasurement';
import {RoomPlannerProductWithTemplate} from 'components/customer/RoomPlanner/types';
import {calculateDisplayHeight} from 'components/customer/RoomPlanner/helpers/cabinetDimension';
import {hasDoors} from 'components/customer/RoomPlanner/helpers/doorDetection';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import {getWallSurfaceBox} from 'components/customer/RoomPlanner/helpers/wallProximity';
import {
    formatMmLabel,
    formatMmLabelFromScaled,
    hasDecimalFromLabels,
} from 'components/customer/RoomPlanner/helpers/formatMeasurementLabel';
import {toRoomConfig} from 'components/customer/RoomPlanner/helpers/measurement/context';
import {
    getActualMeasurementExtents,
    MeasurementConfig,
    MeasurementData,
    WallRefId,
} from 'components/customer/RoomPlanner/helpers/measurement/types';
import {getWallGaps} from 'components/customer/RoomPlanner/helpers/measurement/wallDistance';

// --- bounding.ts ---
/**
 * Wall-local Y offset from cabinet world Y to the elevation floor.
 * Both room types seat the wall mesh at +50.4. The L-shaped floor mesh
 * uses +60 only because its height already includes an extra 20, which
 * cancels back to the same plane. Using 60 here shortens the printed
 * floor gap by 50mm.
 * @return {number} Bottom offset in scene units
 */
export const getCabinetHeightArrowBottomOffset = (): number => 50;

export const shouldMeasureFullCabinetBBoxHeight = (
    cabinet: THREE.Group<THREE.Object3DEventMap>
): boolean =>
    Boolean(cabinet.userData.isPanelProduct) ||
    Boolean(cabinet.userData.isFillerProduct) ||
    Boolean(cabinet.userData.isRobeProduct) ||
    Boolean(cabinet.userData.isVerminProduct) ||
    Boolean(cabinet.userData.isLShapePanel) ||
    Boolean(cabinet.userData.isPlaceholder);

/**
 * Scene-unit X shift applied to cabinet `actualSize` centers so carcase
 * measurements line up. Placeholders have no carcase, so they skip this.
 * Half of this (1.65) is the false overlap between a flush-snapped
 * placeholder and a neighbouring cabinet on the wall strip.
 */
export const CABINET_CARCASE_CENTER_X_NUDGE = 3.3;

/**
 * Cabinets plus appliance placeholders for wall measurement strips.
 * Placeholders live in a separate list and must be merged here.
 * @param {THREE.Group[] | undefined} cabinets - Floor/upper cabinets
 * @param {THREE.Group[] | undefined} placeholders - Fridge/oven placeholders
 * @return {THREE.Group[]} Combined items to measure
 */
export const getItemsForWallMeasurements = (
    cabinets?: THREE.Group<THREE.Object3DEventMap>[],
    placeholders?: THREE.Group<THREE.Object3DEventMap>[]
): THREE.Group<THREE.Object3DEventMap>[] => [
    ...(cabinets ?? []),
    ...(placeholders ?? []),
];

const getPlaceholderSpanLabel = (
    values: {cabinet_width?: number; cabinet_depth?: number} | undefined,
    hasWall: boolean,
    currentDirection: string
): string => {
    const widthLabel = values?.cabinet_width?.toString() ?? '';
    const depthLabel = values?.cabinet_depth?.toString() ?? '';
    if (currentDirection === 'front' || currentDirection === 'back') {
        return hasWall ? depthLabel : widthLabel;
    }
    return hasWall ? widthLabel : depthLabel;
};

/** PDF floor-gap correction: scene placement sits 5mm above the labeled floor. */
export const BOTTOM_EMPTY_GAP_MM_OFFSET = 5;

/**
 * Positions closer than 1mm share one elevation height line.
 * 0.2 scene units is 1mm at the usual 0.2 scale. A gap of 1mm or more
 * steps out to its own line.
 */
export const ELEVATION_HEIGHT_POSITION_TOLERANCE = 0.2;

const elevationPositionGapMm = (left: number, right: number): number => {
    const sceneGap = Math.abs(left - right);
    // Round to 0.001mm so a stored 1mm (0.2 scene units) is not read as
    // 0.999999mm and kept on the same line.
    return (
        Math.round((sceneGap / ELEVATION_HEIGHT_POSITION_TOLERANCE) * 1000) /
        1000
    );
};

/**
 * True when two elevation height rows are the same cabinet size at the same
 * vertical position, so they should share one label and one floor/ceiling pair.
 * A 1mm difference in the top or the bottom is its own line.
 * @param {MeasurementData} left - First height row
 * @param {MeasurementData} right - Second height row
 * @return {boolean} True when the rows belong to one group
 */
export const elevationHeightsMatch = (
    left: {label?: string; start: number; end: number},
    right: {label?: string; start: number; end: number}
): boolean =>
    Boolean(left.label) &&
    left.label === right.label &&
    elevationPositionGapMm(left.start, right.start) < 1 &&
    elevationPositionGapMm(left.end, right.end) < 1;

/**
 * Floor-gap end in scene units for an elevation height row.
 * @param {number} cabinetStart - Wall-local Y of the cabinet bottom
 * @param {number} scale - Room scale
 * @return {number} Gap end, never below the floor
 */
export const getHeightFloorGapEnd = (
    cabinetStart: number,
    scale: number
): number => Math.max(0, cabinetStart - BOTTOM_EMPTY_GAP_MM_OFFSET * scale);

/**
 * Gets the measurement bounds for a cabinet on a wall
 * @param {THREE.Group} cabinet - The cabinet group
 * @param {Object} boundingInfo - Bounding info from getCabinetBoundingInfo
 * @param {MeasurementConfig} config - Measurement configuration
 * @return {Object} Measurement bounds object
 */
export const getCabinetHeightWallMeasurementBounds = (
    cabinet: THREE.Group<THREE.Object3DEventMap>,
    boundingInfo: ReturnType<typeof getCabinetBoundingInfo>,
    config: MeasurementConfig
) => {
    const values = cabinet.userData.values as RoomPlannerProductWithTemplate;
    const scale = config.scale || 1;
    const displayHeightMm = calculateDisplayHeight(cabinet, values);
    const labeledHeight = displayHeightMm * scale;

    const bboxHeight = boundingInfo.size.height;
    const carcaseHeight = boundingInfo.actualSize.height;
    const measureFullHeight = shouldMeasureFullCabinetBBoxHeight(cabinet);
    const fallbackHeight = measureFullHeight ? bboxHeight : carcaseHeight;
    const measurementHeight =
        labeledHeight > 0 ? labeledHeight : fallbackHeight;

    // Same wall-local Y as live 3DRP (cabinet world Y + half room height).
    // Do not use a floor-at-0 shortcut: the wall mesh origin is not the floor,
    // so that placed elevation heights off the cabinets / outside the PDF crop.
    const scaledRoomHeight = config.wallHeight - 20;
    // Placeholders use a GLB pivot that is not at the mesh bottom.
    const originY = cabinet.userData?.isPlaceholder
        ? boundingInfo.bottom
        : cabinet.position.y;
    const bottomWallLocal =
        originY + scaledRoomHeight / 2 - getCabinetHeightArrowBottomOffset();
    const topWallLocal = bottomWallLocal + measurementHeight;

    return {
        width: measurementHeight,
        actualWidth: measurementHeight,
        measurementStart: bottomWallLocal,
        measurementEnd: topWallLocal,
        actualMeasurementStart: bottomWallLocal,
        actualMeasurementEnd: topWallLocal,
        yPosition: bottomWallLocal + measurementHeight / 2,
    };
};

/**
 * Gets cabinet bounding box information for precise measurements
 * @param {THREE.Group<THREE.Object3DEventMap>} cabinet - The cabinet group
 * @param {MeasurementConfig} config - Configuration for measurements
 * @return {Object} Bounding box info with positions and dimensions
 */
export const getCabinetBoundingInfo = (
    cabinet: THREE.Group<THREE.Object3DEventMap>,
    config: MeasurementConfig
) => {
    const boundingBox = computeWorldBoundsExcludingAnimatedDoorParts(cabinet);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    const currentDirection = cabinet?.userData?.currentDirection as string;

    const cabinetValues = cabinet.userData.values as {
        cabinet_width?: number;
        cabinet_depth?: number;
        cabinet_height?: number;
        cabinet_ext_thickness?: number;
        applied_panel_depth?: number;
        cabinet_carc_thickness?: number;
        cabinet_left_width?: number;
        cabinet_right_width?: number;
        width?: number;
        width2?: number;
    };

    const isLShapedFootprint =
        Boolean(cabinet.userData?.isLShapedProduct) &&
        !cabinet.userData?.isPanelProduct &&
        !cabinet.userData?.isPanelProductWithLength &&
        !cabinet.userData?.isPanelProductWithWidth &&
        !cabinet.userData?.isUShapedPanel &&
        !cabinet.userData?.isManualPanel;
    const lShapedRightWidth = isLShapedFootprint
        ? Number(cabinetValues.cabinet_right_width ?? cabinetValues.width) || 0
        : 0;
    const lShapedLeftWidth = isLShapedFootprint
        ? Number(cabinetValues.cabinet_left_width ?? cabinetValues.width2) || 0
        : 0;

    const getActualDimensions = () => {
        const usePlaceholderFallback = Boolean(cabinet.userData?.isPlaceholder);
        const fallbackWidth = usePlaceholderFallback
            ? config.scale
                ? size.x / config.scale
                : size.x
            : 0;
        const fallbackDepth = usePlaceholderFallback
            ? config.scale
                ? size.z / config.scale
                : size.z
            : 0;
        const baseWidth =
            lShapedRightWidth ||
            cabinetValues.cabinet_width ||
            cabinetValues.cabinet_depth ||
            fallbackWidth;
        const baseDepth =
            lShapedLeftWidth || cabinetValues.cabinet_depth || fallbackDepth;
        const baseHeight = cabinetValues.cabinet_height || size.y;

        switch (currentDirection) {
            case 'left':
            case 'right':
                return {
                    width: baseDepth * config.scale,
                    height: baseHeight * config.scale,
                    depth: baseWidth * config.scale,
                };
            default:
                return {
                    width: baseWidth * config.scale,
                    height: baseHeight * config.scale,
                    depth: baseDepth * config.scale,
                };
        }
    };

    const actualDims = getActualDimensions();
    // Compute actual bounding extents analytically using the cabinet's
    // world-space center and the intended actual dimensions, avoiding
    // temporary geometry/mesh creation per cabinet.
    // Must be true center (min+max)/2 — using min + max/2 is wrong and skews
    // actualSize extents when cabinet_width/depth are decimals (e.g. 16.5).
    // Do not subtract door/carc thickness here: that 3.3-unit (16.5mm) shift
    // made print elevation gaps disagree with cabinet_width labels.
    // Placeholders have no carcase, so they use this same center.
    const centerX = (boundingBox.min.x + boundingBox.max.x) / 2;
    const centerY = (boundingBox.min.y + boundingBox.max.y) / 2;
    const centerZ = (boundingBox.min.z + boundingBox.max.z) / 2;

    const halfActualWidth = actualDims.width / 2;
    const halfActualHeight = actualDims.height / 2;
    const halfActualDepth = actualDims.depth / 2;

    return {
        boundingBox,
        left: boundingBox.min.x,
        right: boundingBox.max.x,
        top: boundingBox.max.y,
        bottom: boundingBox.min.y,
        front: boundingBox.max.z,
        back: boundingBox.min.z,
        center: {
            x: centerX,
            y: centerY,
            z: centerZ,
        },
        size: {
            width: size.x,
            height: size.y,
            depth: size.z,
        },
        actualSize: {
            left: centerX - halfActualWidth,
            right: centerX + halfActualWidth,
            top: centerY + halfActualHeight,
            bottom: centerY - halfActualHeight,
            front: centerZ + halfActualDepth,
            back: centerZ - halfActualDepth,
            center: {
                x: centerX,
                y: centerY,
                z: centerZ,
            },
            width: actualDims.width,
            height: actualDims.height,
            depth: actualDims.depth,
        },
    };
};

/**
 * Calculates adjusted bounding box coordinates for L-shaped room measurements
 * @param {Object} boundingInfo - The original bounding info object
 * @param {MeasurementConfig} config - Measurement configuration
 * @param {'leftDepth' | 'rightDepth' | 'rightWidth2' | 'leftWidth2'} adjustmentType - Type of adjustment to apply
 * @return {Object} Object containing adjusted coordinates
 */

export const calculateAdjustedBoundingBox = (
    boundingInfo: {
        left: number;
        right: number;
        back: number;
        front: number;
        actualSize: {
            left: number;
            right: number;
            back: number;
            front: number;
        };
    },
    config: MeasurementConfig,
    adjustmentType: 'leftDepth' | 'rightDepth' | 'rightWidth2' | 'leftWidth2'
): {
    adjustedBoundingInfoLeft: number;
    adjustedBoundingInfoRight: number;
    adjustedBoundingInfoBack: number;
    adjustedBoundingInfoFront: number;
    adjustedActualSizeLeft: number;
    adjustedActualSizeRight: number;
    adjustedActualSizeBack: number;
    adjustedActualSizeFront: number;
} => {
    let adjustedBoundingInfoLeft = boundingInfo.left;
    let adjustedBoundingInfoRight = boundingInfo.right;
    let adjustedBoundingInfoBack = boundingInfo.back;
    let adjustedBoundingInfoFront = boundingInfo.front;
    let adjustedActualSizeLeft = boundingInfo.actualSize.left;
    let adjustedActualSizeRight = boundingInfo.actualSize.right;
    let adjustedActualSizeBack = boundingInfo.actualSize.back;
    let adjustedActualSizeFront = boundingInfo.actualSize.front;

    if (adjustmentType === 'leftDepth') {
        if (config.leftWidth && config.rightDepth && config.scale) {
            const originalRoomLength = config.rightWidth;
            const effectiveRoomLength = config.leftDepth;
            const lengthDifference =
                (originalRoomLength - effectiveRoomLength) * config.scale;

            // Adjust cabinet positions based on lengthDifference
            adjustedBoundingInfoLeft = boundingInfo.left + lengthDifference / 2;
            adjustedBoundingInfoRight =
                boundingInfo.right + lengthDifference / 2;
            adjustedActualSizeLeft =
                boundingInfo.actualSize.left + lengthDifference / 2;
            adjustedActualSizeRight =
                boundingInfo.actualSize.right + lengthDifference / 2;
        }
    } else if (adjustmentType === 'rightDepth') {
        if (config.leftWidth && config.rightDepth && config.scale) {
            const originalRoomLength = config.leftWidth;
            const effectiveRoomLength = config.rightDepth;
            const lengthDifference =
                (originalRoomLength - effectiveRoomLength) * config.scale;

            // Adjust cabinet positions based on lengthDifference
            adjustedBoundingInfoBack = boundingInfo.back + lengthDifference / 2;
            adjustedBoundingInfoFront =
                boundingInfo.front + lengthDifference / 2;
            adjustedActualSizeBack =
                boundingInfo.actualSize.back + lengthDifference / 2;
            adjustedActualSizeFront =
                boundingInfo.actualSize.front + lengthDifference / 2;
        }
    } else if (adjustmentType === 'rightWidth2') {
        if (config.roomWidth && config.scale) {
            const originalRoomLength = config.rightWidth;
            const effectiveRoomLength = config.roomWidth;
            const lengthDifference =
                (originalRoomLength - effectiveRoomLength) * config.scale;

            // Adjust cabinet positions based on lengthDifference
            adjustedBoundingInfoLeft = boundingInfo.left - lengthDifference / 2;
            adjustedBoundingInfoRight =
                boundingInfo.right - lengthDifference / 2;
            adjustedActualSizeLeft =
                boundingInfo.actualSize.left - lengthDifference / 2;
            adjustedActualSizeRight =
                boundingInfo.actualSize.right - lengthDifference / 2;
        }
    } else if (adjustmentType === 'leftWidth2') {
        if (config.roomWidth && config.scale) {
            const originalRoomLength = config.leftWidth;
            const effectiveRoomLength = config.roomWidth;
            const lengthDifference =
                (originalRoomLength - effectiveRoomLength) * config.scale;

            // Adjust cabinet positions based on lengthDifference
            adjustedBoundingInfoLeft = boundingInfo.back - lengthDifference / 2;
            adjustedBoundingInfoRight =
                boundingInfo.front - lengthDifference / 2;
            adjustedActualSizeLeft =
                boundingInfo.actualSize.back - lengthDifference / 2;
            adjustedActualSizeRight =
                boundingInfo.actualSize.front - lengthDifference / 2;
        }
    }

    return {
        adjustedBoundingInfoLeft,
        adjustedBoundingInfoRight,
        adjustedBoundingInfoBack,
        adjustedBoundingInfoFront,
        adjustedActualSizeLeft,
        adjustedActualSizeRight,
        adjustedActualSizeBack,
        adjustedActualSizeFront,
    };
};

export type LShapeLegSide = 'left' | 'right';

const L_SHAPE_LEG_BY_GROUP: ReadonlyMap<string, LShapeLegSide> = new Map([
    ['back|arrowGroupLeft', 'left'],
    ['back|arrowGroupRight', 'right'],
    ['back|arrowGroupBack', 'left'],
    ['back|arrowGroupFront', 'right'],
    ['left|arrowGroupFront', 'left'],
    ['left|arrowGroupRight', 'left'],
    ['left|arrowGroupBack', 'right'],
    ['left|arrowGroupLeft', 'right'],
    ['front|arrowGroupFront', 'left'],
    ['front|arrowGroupRight', 'right'],
    ['front|arrowGroupLeft', 'right'],
    ['front|arrowGroupBack', 'left'],
    ['right|arrowGroupFront', 'left'],
    ['right|arrowGroupRight', 'right'],
    ['right|arrowGroupBack', 'right'],
    ['right|arrowGroupLeft', 'left'],
]);

const L_SHAPE_LEG_BY_WALL: ReadonlyMap<string, LShapeLegSide> = new Map([
    ['leftWall', 'left'],
    ['leftWidthWall', 'left'],
    ['leftWidth2Wall', 'left'],
    ['leftDepthWall', 'left'],
    ['frontWall', 'left'],
    ['rightWall', 'right'],
    ['rightWidthWall', 'right'],
    ['rightWidth2Wall', 'right'],
    ['rightDepthWall', 'right'],
    ['backWall', 'right'],
]);

export const resolveLShapeLegSide = (
    currentDirection: string,
    key: {wallRefId?: string; arrowGroupId?: string}
): LShapeLegSide | undefined => {
    const dir = currentDirection || 'front';
    if (key.arrowGroupId) {
        return L_SHAPE_LEG_BY_GROUP.get(`${dir}|${key.arrowGroupId}`);
    }
    if (key.wallRefId) {
        return L_SHAPE_LEG_BY_WALL.get(key.wallRefId);
    }
    return undefined;
};

export const getLShapeLegWidth = (
    values: RoomPlannerProductWithTemplate,
    currentDirection: string,
    key: {wallRefId?: string; arrowGroupId?: string}
): number | undefined => {
    const leftWidth = values.cabinet_left_width ?? values.width2;
    const rightWidth = values.cabinet_right_width ?? values.width;
    const side = resolveLShapeLegSide(currentDirection, key);
    const legWidth =
        side === 'left' ? leftWidth : side === 'right' ? rightWidth : undefined;
    return typeof legWidth === 'number' && Number.isFinite(legWidth)
        ? legWidth
        : undefined;
};

export const getLShapeLegWidthBySpan = (
    values: RoomPlannerProductWithTemplate,
    measuredWidthMm: number
): number | undefined => {
    const leftNum = Number(values.cabinet_left_width ?? values.width2);
    const rightNum = Number(values.cabinet_right_width ?? values.width);
    const leftValid = Number.isFinite(leftNum);
    const rightValid = Number.isFinite(rightNum);

    if (!leftValid && !rightValid) return undefined;
    if (!leftValid) return rightNum;
    if (!rightValid) return leftNum;
    if (!Number.isFinite(measuredWidthMm)) return undefined;

    return Math.abs(measuredWidthMm - leftNum) <=
        Math.abs(measuredWidthMm - rightNum)
        ? leftNum
        : rightNum;
};

// --- labels.ts ---
/**
 * Returns only the measurement label for one cabinet on the given wall (no positioning/bounding).
 * Mirrors the label logic from generateCabinetMeasurementData for use when only the label is needed.
 *
 * @param {THREE.Group<THREE.Object3DEventMap>} cabinet - Cabinet group
 * @param {MeasurementConfig} config - Measurement config with wallRefId set for the wall
 * @return {string} Label string for this cabinet on this wall
 */
export const getCabinetMeasurementLabel = (
    cabinet: THREE.Group<THREE.Object3DEventMap>,
    config: MeasurementConfig
): string => {
    try {
        const currentDirection = cabinet?.userData?.currentDirection as string;
        const values = cabinet.userData
            .values as RoomPlannerProductWithTemplate;

        const isLeftWall = config.wallRefId === OBJECT_NAMES.LEFT_WALL;
        const isRightWall = config.wallRefId === OBJECT_NAMES.RIGHT_WALL;
        const isBackWall = config.wallRefId === OBJECT_NAMES.BACK_WALL;
        const isFrontWall = config.wallRefId === OBJECT_NAMES.FRONT_WALL;
        const isLeftWidthWall =
            config.wallRefId === OBJECT_NAMES.LEFT_WIDTH_WALL;
        const isRightWidthWall =
            config.wallRefId === OBJECT_NAMES.RIGHT_WIDTH_WALL;
        const isLeftDepthWall =
            config.wallRefId === OBJECT_NAMES.LEFT_DEPTH_WALL;
        const isRightDepthWall =
            config.wallRefId === OBJECT_NAMES.RIGHT_DEPTH_WALL;
        const isLeftWidth2Wall =
            config.wallRefId === OBJECT_NAMES.LEFT_WIDTH2_WALL;
        const isRightWidth2Wall =
            config.wallRefId === OBJECT_NAMES.RIGHT_WIDTH2_WALL;

        let label = '';
        const hasWall =
            isLeftWall ||
            isRightWall ||
            isLeftWidthWall ||
            isRightDepthWall ||
            isLeftWidth2Wall;

        const isManualPanel = cabinet.userData.isManualPanel as boolean;
        if (isManualPanel) {
            const isHorizontalWall =
                isBackWall ||
                isFrontWall ||
                isRightWidthWall ||
                isRightWidth2Wall ||
                isLeftDepthWall;
            const isVerticalWall =
                isLeftWall ||
                isRightWall ||
                isLeftWidthWall ||
                isLeftWidth2Wall ||
                isRightDepthWall;
            const isRotated =
                currentDirection === 'left' || currentDirection === 'right';

            if (isHorizontalWall) {
                label = isRotated
                    ? values?.cabinet_panel_length?.toString() || ''
                    : values?.cabinet_panel_width?.toString() || '';
            } else if (isVerticalWall) {
                label = isRotated
                    ? values?.cabinet_panel_width?.toString() || ''
                    : values?.cabinet_panel_length?.toString() || '';
            } else {
                label = values?.cabinet_panel_width?.toString() || '';
            }
        } else if (cabinet.userData.isPlaceholder) {
            label = getPlaceholderSpanLabel(values, hasWall, currentDirection);
        } else if (
            currentDirection === 'front' ||
            currentDirection === 'back'
        ) {
            if (cabinet.userData.isPanelProduct) {
                if (hasWall) {
                    label =
                        values?.cabinet_applied_panel_depth?.toString() ?? '';
                } else {
                    label = values?.cabinet_ext_thickness?.toString() ?? '';
                }
            } else if (cabinet.userData.isPanelProductWithLength) {
                if (hasWall) {
                    label = values?.cabinet_panel_length?.toString() ?? '';
                } else {
                    label = values?.cabinet_carc_thickness?.toString() ?? '';
                }
            } else if (cabinet.userData.isPanelProductWithWidth) {
                if (hasWall) {
                    label = values?.cabinet_panel_width?.toString() ?? '';
                } else {
                    label = values?.cabinet_carc_thickness?.toString() ?? '';
                }
            } else if (cabinet.userData.isUShapedPanel) {
                if (hasWall) {
                    label = values?.cabinet_panel_width?.toString() ?? '';
                } else {
                    label = values?.cabinet_panel_length?.toString() ?? '';
                }
            } else if (hasWall) {
                if (cabinet.userData.isLShapedProduct) {
                    label = values?.width2?.toString() ?? '';
                } else {
                    label = values?.cabinet_depth?.toString() ?? '';
                }
            } else {
                label = values?.cabinet_width?.toString() ?? '';
            }
        } else {
            if (cabinet.userData.isPanelProduct) {
                if (
                    isLeftWall ||
                    isRightWall ||
                    isLeftWidth2Wall ||
                    isRightDepthWall
                ) {
                    label = values?.cabinet_ext_thickness?.toString() ?? '';
                } else {
                    label =
                        values?.cabinet_applied_panel_depth?.toString() ?? '';
                }
            } else if (cabinet.userData.isPanelProductWithLength) {
                if (isLeftWall || isRightWall || isLeftWidth2Wall) {
                    label = values?.cabinet_carc_thickness?.toString() ?? '';
                } else {
                    label = values?.cabinet_panel_length?.toString() ?? '';
                }
            } else if (cabinet.userData.isPanelProductWithWidth) {
                if (hasWall) {
                    label = values?.cabinet_carc_thickness?.toString() ?? '';
                } else {
                    label = values?.cabinet_panel_width?.toString() ?? '';
                }
            } else if (cabinet.userData.isUShapedPanel) {
                if (hasWall) {
                    label = values?.cabinet_panel_length?.toString() ?? '';
                } else {
                    label = values?.cabinet_panel_width?.toString() ?? '';
                }
            } else if (hasWall) {
                label = values?.cabinet_width?.toString() ?? '';
            } else if (cabinet.userData.isLShapedProduct) {
                label = values?.width2?.toString() ?? '';
            } else {
                label = values?.cabinet_depth?.toString() ?? '';
            }
        }

        if (
            cabinet.userData.isLShapedProduct &&
            !isManualPanel &&
            !cabinet.userData.isPanelProduct &&
            !cabinet.userData.isPanelProductWithLength &&
            !cabinet.userData.isPanelProductWithWidth &&
            !cabinet.userData.isUShapedPanel
        ) {
            const legWidth = getLShapeLegWidth(values, currentDirection, {
                wallRefId: config.wallRefId,
            });
            if (typeof legWidth === 'number') {
                label = `${legWidth}`;
            }
        }

        const isFrontElevation =
            config.isForPrinting && config.previewPosition === 'FRONT';
        const isLeftElevation =
            config.isForPrinting && config.previewPosition === 'RIGHT_LEVELED';
        const isRightElevation =
            config.isForPrinting && config.previewPosition === 'LEFT_LEVELED';
        const displayHeightOnWall =
            (isFrontElevation && (isLeftWall || isLeftWidthWall)) ||
            (isLeftElevation && (isFrontWall || isLeftDepthWall)) ||
            (isRightElevation && (isBackWall || isRightWidthWall));
        if (displayHeightOnWall) {
            label = `${calculateDisplayHeight(cabinet, values)}`;
        }

        return label;
    } catch {
        return '';
    }
};

/**
 * Returns only the measurement labels for all cabinets on the given wall (no positioning/bounding).
 * Use when you only need labels (e.g. to check for decimals) and want to avoid full measurement data calculation.
 *
 * @param {THREE.Group<THREE.Object3DEventMap>[]} cabinetGroupList - Array of cabinet groups
 * @param {MeasurementConfig} config - Measurement config with wallRefId set for the wall
 * @return {string[]} Array of label strings
 */
export const getCabinetMeasurementLabelsForWall = (
    cabinetGroupList: THREE.Group<THREE.Object3DEventMap>[],
    config: MeasurementConfig
): string[] =>
    cabinetGroupList.map((cabinet) =>
        getCabinetMeasurementLabel(cabinet, config)
    );

/** All walls with their orientation (depth = vertical, width = horizontal in room terms). */
const WALL_ORIENTATIONS: Array<{
    wallRefId: string;
    orientation: 'vertical' | 'horizontal';
}> = [
    {wallRefId: OBJECT_NAMES.LEFT_WALL, orientation: 'vertical'},
    {wallRefId: OBJECT_NAMES.RIGHT_WALL, orientation: 'vertical'},
    {wallRefId: OBJECT_NAMES.LEFT_WIDTH_WALL, orientation: 'vertical'},
    {wallRefId: OBJECT_NAMES.LEFT_WIDTH2_WALL, orientation: 'vertical'},
    {wallRefId: OBJECT_NAMES.RIGHT_DEPTH_WALL, orientation: 'vertical'},
    {wallRefId: OBJECT_NAMES.BACK_WALL, orientation: 'horizontal'},
    {wallRefId: OBJECT_NAMES.FRONT_WALL, orientation: 'horizontal'},
    {wallRefId: OBJECT_NAMES.RIGHT_WIDTH_WALL, orientation: 'horizontal'},
    {wallRefId: OBJECT_NAMES.RIGHT_WIDTH2_WALL, orientation: 'horizontal'},
    {wallRefId: OBJECT_NAMES.LEFT_DEPTH_WALL, orientation: 'horizontal'},
];

/**
 * Gets measurement data for every wall, checks which walls have a label with a decimal,
 * then classifies each wall as vertical or horizontal and returns whether any vertical/horizontal wall has a decimal.
 * Use this to decide whether to show wall distance labels with decimals (e.g. in createRoomObjects onDrag).
 *
 * @param {THREE.Group<THREE.Object3DEventMap>[]} cabinetGroupList - Array of cabinet groups
 * @param {MeasurementConfig} config - Measurement config (room dimensions, scale, wallHeight, etc.)
 * @return {{vertical: boolean, horizontal: boolean}} vertical true if any vertical wall has a decimal in labels, horizontal same for horizontal walls
 */
export const getHasDecimalInMeasurementLabelsByOrientation = (
    cabinetGroupList: THREE.Group<THREE.Object3DEventMap>[],
    config: MeasurementConfig
): {vertical: boolean; horizontal: boolean} => {
    let hasVerticalDecimal = false;
    let hasHorizontalDecimal = false;

    for (const {wallRefId, orientation} of WALL_ORIENTATIONS) {
        const wallConfig: MeasurementConfig = {...config, wallRefId};
        const labels = getCabinetMeasurementLabelsForWall(
            cabinetGroupList,
            wallConfig
        ).filter(Boolean);

        const wallHasDecimal = hasDecimalFromLabels(labels);
        if (wallHasDecimal) {
            if (orientation === 'vertical') {
                hasVerticalDecimal = true;
            } else {
                hasHorizontalDecimal = true;
            }
        }
    }

    return {
        vertical: hasVerticalDecimal,
        horizontal: hasHorizontalDecimal,
    };
};

// --- wallUtils.ts ---
// Wall type detection helper
export interface WallType {
    isLeftWall: boolean;
    isRightWall: boolean;
    isBackWall: boolean;
    isFrontWall: boolean;
    isLeftWidthWall: boolean;
    isRightWidthWall: boolean;
    isLeftDepthWall: boolean;
    isRightDepthWall: boolean;
    isRightWidth2Wall: boolean;
    isLeftWidth2Wall: boolean;
}

export const getWallType = (wallRefId?: string): WallType => {
    return {
        isLeftWall: wallRefId === OBJECT_NAMES.LEFT_WALL,
        isRightWall: wallRefId === OBJECT_NAMES.RIGHT_WALL,
        isBackWall: wallRefId === OBJECT_NAMES.BACK_WALL,
        isFrontWall: wallRefId === OBJECT_NAMES.FRONT_WALL,
        isLeftWidthWall: wallRefId === OBJECT_NAMES.LEFT_WIDTH_WALL,
        isRightWidthWall: wallRefId === OBJECT_NAMES.RIGHT_WIDTH_WALL,
        isLeftDepthWall: wallRefId === OBJECT_NAMES.LEFT_DEPTH_WALL,
        isRightDepthWall: wallRefId === OBJECT_NAMES.RIGHT_DEPTH_WALL,
        isRightWidth2Wall: wallRefId === OBJECT_NAMES.RIGHT_WIDTH2_WALL,
        isLeftWidth2Wall: wallRefId === OBJECT_NAMES.LEFT_WIDTH2_WALL,
    };
};

/**
 * Distance tolerance (scene units) for treating a cabinet as clamped to a
 * wall. Same tolerance as the PDF elevation view filters.
 */
export const WALL_CLAMP_MEASUREMENT_THRESHOLD = 15;

/**
 * True when the cabinet sits against the wall (clamped), mirroring the
 * clamped-to-wall filter used by the PDF elevation views. Measures the gap
 * between the wall surface and cabinet bounding box along the wall's
 * perpendicular axis; touching or slightly penetrating cabinets count as
 * clamped. Uses the geometry-derived wall box (getWallSurfaceBox) because
 * walls carry dimension-arrow children that inflate a setFromObject AABB
 * far into the room.
 * @param {THREE.Object3D} cabinet - Cabinet group
 * @param {THREE.Mesh | null | undefined} wall - Wall mesh
 * @param {'x' | 'z'} axis - Axis perpendicular to the wall face
 * @return {boolean} True when the cabinet is clamped to the wall
 */
export const isCabinetClampedToWall = (
    cabinet: THREE.Object3D<THREE.Object3DEventMap>,
    wall: THREE.Mesh | null | undefined,
    axis: 'x' | 'z'
): boolean => {
    if (!wall) return false;
    const wallBox = getWallSurfaceBox(wall);
    const cabinetBox = cabinet.userData?.isPlaceholder
        ? computeWorldBoundsExcludingAnimatedDoorParts(
              cabinet as THREE.Group<THREE.Object3DEventMap>
          )
        : new THREE.Box3().setFromObject(cabinet);

    const gap =
        axis === 'z'
            ? Math.max(
                  wallBox.min.z - cabinetBox.max.z,
                  cabinetBox.min.z - wallBox.max.z
              )
            : Math.max(
                  wallBox.min.x - cabinetBox.max.x,
                  cabinetBox.min.x - wallBox.max.x
              );

    // Negative gap means the boxes overlap on that axis (cabinet touching or
    // slightly inside the wall).
    return gap < WALL_CLAMP_MEASUREMENT_THRESHOLD;
};

/**
 * Checks if a wall should be hidden based on hiddenWall state and room type
 * Only applies when not printing
 * @param {string} wallId - The ID of the wall to check
 * @param {string | null | undefined} hiddenWall - Comma-separated string of hidden wall IDs
 * @param {string} roomType - The type of room ('RECTANGULAR' or 'LSHAPED')
 * @param {boolean} isForPrinting - Whether the view is for printing (walls are never hidden when printing)
 * @return {boolean} True if the wall should be hidden, false otherwise
 */
export const isWallHidden = (
    wallId: string,
    hiddenWall: string | null | undefined,
    roomType: string,
    isForPrinting: boolean
): boolean => {
    // Don't hide walls when printing
    if (isForPrinting || !hiddenWall) {
        return false;
    }

    const hiddenWalls = hiddenWall.split(',').map((wall) => wall.trim());
    if (hiddenWalls.length > 2) return false;

    if (roomType === 'RECTANGULAR') {
        return hiddenWalls.includes(wallId);
    }

    if (roomType === 'LSHAPED') {
        // Apply the same logic as useWallHider for L-shaped rooms
        const wallsToHide: string[] = [];

        hiddenWalls.forEach((hiddenWallId) => {
            switch (hiddenWallId) {
                case 'leftWall':
                    wallsToHide.push('leftWidthWall');
                    break;
                case 'frontWall':
                    const isLeftMainWall =
                        hiddenWalls && hiddenWalls.length > 1
                            ? hiddenWalls[0] === 'leftWall'
                            : false;

                    if (isLeftMainWall) {
                        wallsToHide.push('leftDepthWall');
                    } else {
                        wallsToHide.push('leftDepthWall');
                        wallsToHide.push('leftWidth2Wall');
                        wallsToHide.push('rightWidth2Wall');
                    }
                    break;
                case 'rightWall':
                    const isBackMainWall =
                        hiddenWalls && hiddenWalls.length > 1
                            ? hiddenWalls[0] === 'backWall'
                            : false;

                    if (isBackMainWall) {
                        wallsToHide.push('rightDepthWall');
                    } else {
                        wallsToHide.push('rightDepthWall');
                        wallsToHide.push('rightWidth2Wall');
                        wallsToHide.push('leftWidth2Wall');
                    }
                    break;
                default:
                    wallsToHide.push('rightWidthWall');
                    break;
            }
        });

        return wallsToHide.includes(wallId);
    }

    return false;
};

export const getWallBoundaries = (config: MeasurementConfig) => {
    // Use the centralized wall type detection
    const wallType = getWallType(config.wallRefId);
    const {
        isLeftWall,
        isRightWall,
        isBackWall,
        isFrontWall,
        isLeftWidthWall,
        isRightWidthWall,
        isLeftDepthWall,
        isRightDepthWall,
        isRightWidth2Wall,
        isLeftWidth2Wall,
    } = wallType;

    // Determine room dimension based on wall type
    let roomDimension: number | undefined;

    if (isLeftWall || isRightWall) {
        // Rectangular room left/right walls use roomLength
        roomDimension = config.roomLength;
    } else if (isBackWall || isFrontWall) {
        // Rectangular room front/back walls use roomWidth
        roomDimension = config.roomWidth;
    } else if (isLeftWidthWall) {
        // L-shaped left width wall uses leftWidth
        roomDimension = config.leftWidth;
    } else if (isRightWidthWall) {
        // L-shaped right width wall uses rightWidth
        roomDimension = config.rightWidth;
    } else if (isLeftDepthWall) {
        // L-shaped left depth wall uses leftDepth
        roomDimension = config.leftDepth;
    } else if (isRightDepthWall) {
        // L-shaped right depth wall uses rightDepth
        roomDimension = config.rightDepth;
    } else if (isRightWidth2Wall) {
        // L-shaped right width2 wall is the inner notch segment.
        // Its length is rightWidth - leftDepth (matches the wall geometry
        // built in useRoomLayout), NOT the full room width.
        roomDimension =
            config.rightWidth != null && config.leftDepth != null
                ? config.rightWidth - config.leftDepth
                : config.roomWidth;
    } else if (isLeftWidth2Wall) {
        // L-shaped left width2 wall is the inner notch segment.
        // Its length is leftWidth - rightDepth (matches the wall geometry
        // built in useRoomLayout), NOT the full room width.
        roomDimension =
            config.leftWidth != null && config.rightDepth != null
                ? config.leftWidth - config.rightDepth
                : config.roomWidth;
    }

    if (!roomDimension || !config.scale) {
        return {leftWall: 0, rightWall: 0, center: 0, fullWidth: 0};
    }

    // Use the same coordinate system as the old implementation
    // wallStart = 0, wallEnd = roomDimension * scale
    const roomFullWidth = roomDimension * config.scale;

    return {
        leftWall: 0, // wallStart position (left wall)
        rightWall: roomFullWidth, // wallEnd position (right wall)
        center: roomFullWidth / 2, // Room center
        fullWidth: roomFullWidth, // Total room width
    };
};

// --- cabinetMeasurementData.ts ---
/**
 * Generates measurement data from cabinet groups using room coordinate system
 * @param {THREE.Group<THREE.Object3DEventMap>[]} cabinetGroupList - Array of cabinet groups
 * @param {MeasurementConfig} config - Configuration for measurements
 * @param {('cabinet' | 'emptySpace' | 'wall')} type - Type of measurement for styling and behavior
 * @param {THREE.Group<THREE.Object3DEventMap>} cabinetGroup - Cabinet group for active status
 * @return {MeasurementData[]} Array of measurement data
 *
 */
export const generateCabinetMeasurementData = (
    cabinetGroupList: THREE.Group<THREE.Object3DEventMap>[],
    config: MeasurementConfig,
    type: 'cabinet' | 'emptySpace',
    cabinetGroup?: THREE.Group<THREE.Object3DEventMap>
): MeasurementData[] => {
    return cabinetGroupList.map((cabinet, index) => {
        try {
            const boundingInfo = getCabinetBoundingInfo(cabinet, config);
            const cabinetId =
                (cabinet.userData.id as string) || `cabinet-${index}`;
            const currentDirection = cabinet?.userData
                ?.currentDirection as string;

            const cabinetHasDoors = hasDoors(cabinet);

            const values = cabinet.userData
                .values as RoomPlannerProductWithTemplate;

            const doorThickness =
                cabinetHasDoors && !cabinet.userData.isLShapedProduct
                    ? (values?.cabinet_ext_thickness || 0) * config.scale || 0
                    : -0.4;

            // Handle different wall types - separate logic for left and right walls
            const isLeftWall = config.wallRefId === OBJECT_NAMES.LEFT_WALL;
            const isRightWall = config.wallRefId === OBJECT_NAMES.RIGHT_WALL;
            const isBackWall = config.wallRefId === OBJECT_NAMES.BACK_WALL;
            const isFrontWall = config.wallRefId === OBJECT_NAMES.FRONT_WALL;

            // LShaped Walls
            const isLeftWidthWall =
                config.wallRefId === OBJECT_NAMES.LEFT_WIDTH_WALL;
            const isRightWidthWall =
                config.wallRefId === OBJECT_NAMES.RIGHT_WIDTH_WALL;
            const isLeftDepthWall =
                config.wallRefId === OBJECT_NAMES.LEFT_DEPTH_WALL;
            const isRightDepthWall =
                config.wallRefId === OBJECT_NAMES.RIGHT_DEPTH_WALL;
            const isLeftWidth2Wall =
                config.wallRefId === OBJECT_NAMES.LEFT_WIDTH2_WALL;
            const isRightWidth2Wall =
                config.wallRefId === OBJECT_NAMES.RIGHT_WIDTH2_WALL;

            // Choose label based on cabinet direction
            let label = '';
            const hasWall =
                isLeftWall ||
                isRightWall ||
                isLeftWidthWall ||
                isRightDepthWall ||
                isLeftWidth2Wall;

            // Manual panel handling - must come before other product type checks
            const isManualPanel = cabinet.userData.isManualPanel as boolean;
            if (isManualPanel) {
                // Manual panels use form values: cabinet_panel_width and cabinet_panel_length
                // Need to determine which dimension is visible based on wall orientation and panel rotation

                // Determine wall orientation
                const isHorizontalWall =
                    isBackWall ||
                    isFrontWall ||
                    isRightWidthWall ||
                    isRightWidth2Wall ||
                    isLeftDepthWall;
                const isVerticalWall =
                    isLeftWall ||
                    isRightWall ||
                    isLeftWidthWall ||
                    isLeftWidth2Wall ||
                    isRightDepthWall;

                // Determine if panel is rotated (facing left or right)
                const isRotated =
                    currentDirection === 'left' || currentDirection === 'right';

                if (isHorizontalWall) {
                    // Horizontal walls measure along X-axis
                    // When rotated, panel's length aligns with X-axis; when not rotated, width aligns with X-axis
                    label = isRotated
                        ? values?.cabinet_panel_length?.toString() || ''
                        : values?.cabinet_panel_width?.toString() || '';
                } else if (isVerticalWall) {
                    // Vertical walls measure along Z-axis
                    // When rotated, panel's width aligns with Z-axis; when not rotated, length aligns with Z-axis
                    label = isRotated
                        ? values?.cabinet_panel_width?.toString() || ''
                        : values?.cabinet_panel_length?.toString() || '';
                } else {
                    // Fallback for unknown wall types
                    label = values?.cabinet_panel_width?.toString() || '';
                }
            } else if (cabinet.userData.isPlaceholder) {
                label = getPlaceholderSpanLabel(
                    values,
                    hasWall,
                    currentDirection
                );
            } else if (
                currentDirection === 'front' ||
                currentDirection === 'back'
            ) {
                if (
                    cabinet.userData.isUpperProduct &&
                    cabinet.userData.isVerminProduct
                ) {
                    if (hasWall) {
                        label =
                            values?.cabinet_upper_filler_depth?.toString() ??
                            '';
                    } else {
                        label = values?.cabinet_width?.toString() ?? '';
                    }
                } else if (cabinet.userData.isPanelProduct) {
                    if (hasWall) {
                        label = values?.cabinet_applied_panel_depth?.toString();
                    } else {
                        label = values?.cabinet_ext_thickness?.toString();
                    }
                } else if (cabinet.userData.isPanelProductWithLength) {
                    if (hasWall) {
                        label = values?.cabinet_panel_length?.toString();
                    } else {
                        label = values?.cabinet_carc_thickness?.toString();
                    }
                } else if (cabinet.userData.isPanelProductWithWidth) {
                    if (hasWall) {
                        label = values?.cabinet_panel_width?.toString();
                    } else {
                        label = values?.cabinet_carc_thickness?.toString();
                    }
                } else if (cabinet.userData.isUShapedPanel) {
                    if (hasWall) {
                        label = values?.cabinet_panel_width?.toString();
                    } else {
                        label = values?.cabinet_panel_length?.toString();
                    }
                } else if (cabinet.userData.isFillerProduct) {
                    if (hasWall) {
                        label = formatMmLabelFromScaled(
                            boundingInfo.size.depth,
                            config.scale
                        );
                    } else {
                        label = values?.cabinet_width?.toString() ?? '';
                    }
                } else if (hasWall) {
                    if (cabinet.userData.isLShapedProduct) {
                        label = values?.width2?.toString();
                    } else {
                        label = values?.cabinet_depth?.toString();
                    }
                } else {
                    label = values?.cabinet_width?.toString();
                }
            } else {
                if (cabinet.userData.isPanelProduct) {
                    if (
                        isLeftWall ||
                        isRightWall ||
                        isLeftWidth2Wall ||
                        isRightDepthWall
                    ) {
                        label = values?.cabinet_ext_thickness?.toString();
                    } else {
                        label = values?.cabinet_applied_panel_depth?.toString();
                    }
                } else if (cabinet.userData.isPanelProductWithLength) {
                    if (isLeftWall || isRightWall || isLeftWidth2Wall) {
                        label = values?.cabinet_carc_thickness?.toString();
                    } else {
                        label = values?.cabinet_panel_length?.toString();
                    }
                } else if (cabinet.userData.isPanelProductWithWidth) {
                    if (hasWall) {
                        label = values?.cabinet_carc_thickness?.toString();
                    } else {
                        label = values?.cabinet_panel_width?.toString();
                    }
                } else if (cabinet.userData.isUShapedPanel) {
                    if (hasWall) {
                        label = values?.cabinet_panel_length?.toString();
                    } else {
                        label = values?.cabinet_panel_width?.toString();
                    }
                } else if (hasWall) {
                    label = values?.cabinet_width?.toString();
                } else if (cabinet.userData.isLShapedProduct) {
                    label = values?.width2?.toString();
                } else {
                    label = values?.cabinet_depth?.toString();
                }
            }

            const isFrontElevation =
                config.isForPrinting && config.previewPosition === 'FRONT';
            const isLeftElevation =
                config.isForPrinting &&
                config.previewPosition === 'RIGHT_LEVELED';
            const isRightElevation =
                config.isForPrinting &&
                config.previewPosition === 'LEFT_LEVELED';
            const displayHeightOnWall =
                (isFrontElevation && (isLeftWall || isLeftWidthWall)) ||
                (isLeftElevation && (isFrontWall || isLeftDepthWall)) ||
                (isRightElevation && (isBackWall || isRightWidthWall));
            if (displayHeightOnWall) {
                label = `${calculateDisplayHeight(cabinet, values)}`;
            }

            let measurementStart: number;
            let measurementEnd: number;
            let actualMeasurementStart: number;
            let actualMeasurementEnd: number;
            let xPosition: number;
            let yPosition: number;
            let actualPostionX: number;
            let width = 0;
            let actualWidth = 0;

            if (displayHeightOnWall) {
                const heightBounds = getCabinetHeightWallMeasurementBounds(
                    cabinet,
                    boundingInfo,
                    config
                );
                width = heightBounds.width;
                actualWidth = heightBounds.actualWidth;
                measurementStart = heightBounds.measurementStart;
                measurementEnd = heightBounds.measurementEnd;
                actualMeasurementStart = heightBounds.actualMeasurementStart;
                actualMeasurementEnd = heightBounds.actualMeasurementEnd;
                yPosition = heightBounds.yPosition;
                xPosition = 0;
                actualPostionX = 0;
            } else if (isLeftWall) {
                width = boundingInfo.size.depth; // Use depth for left wall
                actualWidth = boundingInfo.actualSize.depth;
                const offset = type === 'cabinet' ? -width / 2 + width + 47 : 0;

                const actualOffset =
                    type === 'cabinet'
                        ? actualWidth - (currentDirection === 'left' ? 0 : 12)
                        : 0;

                // Calculate xPosition using room coordinate system for z-axis (LEFT WALL)
                if (config.roomLength && config.scale) {
                    xPosition =
                        -boundingInfo.back +
                        (config.roomLength * config.scale) / 2 +
                        35 -
                        offset;
                    actualPostionX =
                        -boundingInfo.actualSize.back +
                        (config.roomLength * config.scale) / 2 +
                        35 -
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet z-positions to wall coordinate system (LEFT WALL)
                const roomCenter = (config.roomLength * config.scale) / 2;

                measurementStart = boundingInfo.back + roomCenter;
                measurementEnd = boundingInfo.front + roomCenter;

                actualMeasurementStart = boundingInfo.back + roomCenter;
                actualMeasurementEnd = boundingInfo.front + roomCenter;
            } else if (isRightWall) {
                // For right wall, use cabinet's z-position
                width = boundingInfo.size.depth; // Use depth for left wall
                actualWidth = boundingInfo.actualSize.depth;
                const offset =
                    type === 'cabinet'
                        ? currentDirection === 'right'
                            ? -width / 2 + width + 22.5
                            : boundingInfo.size.depth / 2 + doorThickness + 20
                        : 0;

                const actualOffset =
                    type === 'cabinet'
                        ? actualWidth - (currentDirection === 'left' ? 0 : 12)
                        : 0;

                // Calculate xPosition using room coordinate system for z-axis (LEFT WALL)
                if (config.roomLength && config.scale) {
                    xPosition =
                        -boundingInfo.back +
                        (config.roomLength * config.scale) / 2 +
                        35 -
                        offset;
                    actualPostionX =
                        -boundingInfo.actualSize.back +
                        (config.roomLength * config.scale) / 2 +
                        35 -
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet z-positions to wall coordinate system (LEFT WALL)
                const roomCenter = (config.roomLength * config.scale) / 2;

                measurementStart = boundingInfo.back + roomCenter;
                measurementEnd = boundingInfo.front + roomCenter;

                actualMeasurementStart = boundingInfo.back + roomCenter;
                actualMeasurementEnd = boundingInfo.front + roomCenter;
            } else if (isBackWall || isFrontWall) {
                // For front/back walls, use cabinet's x-position (original logic)

                width = boundingInfo.size.width;
                actualWidth = boundingInfo.actualSize.width;

                const actualOffset = type === 'cabinet' ? actualWidth / 2 : 0;

                // Calculate xPosition using room coordinate system (center = 0)
                if (config.roomWidth && config.scale) {
                    xPosition =
                        -boundingInfo.actualSize.left +
                        (config.roomWidth * config.scale) / 2 +
                        35 -
                        actualOffset;
                    actualPostionX =
                        -boundingInfo.left +
                        (config.roomWidth * config.scale) / 2 +
                        35 -
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet positions from room coordinates to wall coordinates
                const roomCenter = (config.roomWidth * config.scale) / 2;

                measurementStart = boundingInfo.left + roomCenter;
                measurementEnd = boundingInfo.right + roomCenter;
                actualMeasurementStart =
                    boundingInfo.actualSize.left + roomCenter;
                actualMeasurementEnd =
                    boundingInfo.actualSize.right + roomCenter;
            }

            // // LShaped Walls
            else if (isLeftWidthWall) {
                // For left wall, use cabinet's z-position
                width = boundingInfo.size.depth; // Use depth for left wall
                actualWidth = boundingInfo.actualSize.depth;
                const offset =
                    type === 'cabinet'
                        ? currentDirection === 'right'
                            ? width / 2 + width + 22.5
                            : width / 2 + 47
                        : 0;

                const actualOffset =
                    type === 'cabinet'
                        ? actualWidth - (currentDirection === 'left' ? 0 : 12)
                        : 0;

                // Calculate xPosition using room coordinate system for z-axis (LEFT WALL)
                if (config.leftWidth && config.scale) {
                    xPosition =
                        -boundingInfo.back +
                        (config.leftWidth * config.scale) / 2 +
                        35 -
                        offset;
                    actualPostionX =
                        -boundingInfo.actualSize.back +
                        (config.leftWidth * config.scale) / 2 +
                        35 -
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet z-positions to wall coordinate system (LEFT WALL)
                const roomCenter = (config.leftWidth * config.scale) / 2;

                measurementStart = boundingInfo.back + roomCenter;
                measurementEnd = boundingInfo.front + roomCenter;

                actualMeasurementStart = boundingInfo.back + roomCenter;
                actualMeasurementEnd = boundingInfo.front + roomCenter;
            } else if (isRightWidthWall) {
                // For right width wall, use cabinet's z-position (similar to left width wall)
                width = boundingInfo.size.width;
                actualWidth = boundingInfo.actualSize.width;

                const offset = type === 'cabinet' ? width / 2 - 25 : 0;
                const actualOffset = type === 'cabinet' ? actualWidth / 2 : 0;

                // Calculate xPosition using room coordinate system (center = 0)

                if (config.rightWidth && config.scale) {
                    xPosition =
                        -boundingInfo.left +
                        (config.rightWidth * config.scale) / 2 +
                        35 -
                        offset;
                    actualPostionX =
                        -boundingInfo.left +
                        (config.rightWidth * config.scale) / 2 +
                        35 -
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet positions from room coordinates to wall coordinates
                const roomCenter = (config.rightWidth * config.scale) / 2;
                measurementStart = boundingInfo.left + roomCenter;
                measurementEnd = boundingInfo.right + roomCenter;
                actualMeasurementStart =
                    boundingInfo.actualSize.left + roomCenter;
                actualMeasurementEnd =
                    boundingInfo.actualSize.right + roomCenter;
            } else if (isLeftDepthWall) {
                // For left depth wall, use cabinet's x-position (similar to front/back walls)
                width = boundingInfo.size.width;
                actualWidth = boundingInfo.actualSize.width;

                const offset = type === 'cabinet' ? -width / 2 + 47 : 0;
                const actualOffset = type === 'cabinet' ? actualWidth / 2 : 0;

                // Apply lengthDifference adjustment for L-shaped rooms (same logic as createRoomObjects.ts)
                const adjustedBoundingBox = calculateAdjustedBoundingBox(
                    boundingInfo,
                    config,
                    'leftDepth'
                );
                const {
                    adjustedBoundingInfoLeft,
                    adjustedBoundingInfoRight,
                    adjustedActualSizeLeft,
                    adjustedActualSizeRight,
                } = adjustedBoundingBox;

                // Calculate xPosition using room coordinate system (center = 0)
                if (config.leftDepth && config.scale) {
                    xPosition =
                        adjustedBoundingInfoLeft +
                        (config.leftDepth * config.scale) / 2 +
                        35 -
                        offset;
                    actualPostionX =
                        adjustedActualSizeLeft +
                        (config.leftDepth * config.scale) / 2 +
                        35 -
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet positions from room coordinates to wall coordinates
                const roomCenter = (config.leftDepth * config.scale) / 2;
                measurementStart = adjustedBoundingInfoLeft + roomCenter;
                measurementEnd = adjustedBoundingInfoRight + roomCenter;
                actualMeasurementStart = adjustedActualSizeLeft + roomCenter;
                actualMeasurementEnd = adjustedActualSizeRight + roomCenter;
            } else if (isRightDepthWall) {
                // For right depth wall, use cabinet's x-position (similar to front/back walls)
                // For right wall, use cabinet's z-position
                width = boundingInfo.size.depth; // Use depth for left wall
                actualWidth = boundingInfo.actualSize.depth;
                const offset =
                    type === 'cabinet'
                        ? currentDirection === 'right'
                            ? -width / 2 + 22.5
                            : -boundingInfo.size.depth / 2 + doorThickness + 20
                        : 0;

                const actualOffset =
                    type === 'cabinet'
                        ? actualWidth - (currentDirection === 'left' ? 0 : 12)
                        : 0;

                // Apply lengthDifference adjustment for L-shaped rooms (same logic as createRoomObjects.ts)
                const adjustedBoundingBox = calculateAdjustedBoundingBox(
                    boundingInfo,
                    config,
                    'rightDepth'
                );
                const {
                    adjustedBoundingInfoBack,
                    adjustedBoundingInfoFront,
                    adjustedActualSizeBack,
                    adjustedActualSizeFront,
                } = adjustedBoundingBox;

                // Calculate xPosition using room coordinate system for z-axis (LEFT WALL)
                if (config.rightDepth && config.scale) {
                    xPosition =
                        adjustedBoundingInfoBack +
                        (config.rightDepth * config.scale) / 2 +
                        35 -
                        offset;
                    actualPostionX =
                        adjustedActualSizeBack +
                        (config.rightDepth * config.scale) / 2 +
                        35 -
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet z-positions to wall coordinate system (LEFT WALL)
                const roomCenter = (config.rightDepth * config.scale) / 2;

                measurementStart = adjustedBoundingInfoBack + roomCenter;
                measurementEnd = adjustedBoundingInfoFront + roomCenter;

                actualMeasurementStart = adjustedActualSizeBack + roomCenter;
                actualMeasurementEnd = adjustedActualSizeFront + roomCenter;
            } else if (isRightWidth2Wall) {
                // For right depth wall, use cabinet's x-position (similar to front/back walls)
                // For right wall, use cabinet's z-position
                width = boundingInfo.size.width;
                actualWidth = boundingInfo.actualSize.width;

                const offset =
                    type === 'cabinet'
                        ? -width / 2 - 22.5
                        : type === 'emptySpace'
                          ? -width - 22.5
                          : 0;
                const actualOffset =
                    type === 'cabinet'
                        ? -actualWidth / 2
                        : type === 'emptySpace'
                          ? -actualWidth / 2 - 22.5
                          : 0;

                // Apply lengthDifference adjustment for L-shaped rooms (same logic as createRoomObjects.ts)
                const adjustedBoundingBox = calculateAdjustedBoundingBox(
                    boundingInfo,
                    config,
                    'rightWidth2'
                );
                const {
                    adjustedBoundingInfoLeft,
                    adjustedBoundingInfoRight,
                    adjustedActualSizeLeft,
                    adjustedActualSizeRight,
                } = adjustedBoundingBox;

                // Calculate xPosition using room coordinate system (center = 0)
                if (config.roomWidth && config.scale) {
                    xPosition =
                        -adjustedBoundingInfoRight +
                        (config.roomWidth * config.scale) / 2 +
                        35 -
                        offset;
                    actualPostionX =
                        -adjustedBoundingInfoRight -
                        (config.roomWidth * config.scale) / 2 +
                        35 +
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet positions from room coordinates to wall coordinates
                const roomCenter = (config.roomWidth * config.scale) / 2;
                measurementStart = adjustedBoundingInfoLeft + roomCenter;
                measurementEnd = adjustedBoundingInfoRight + roomCenter;
                actualMeasurementStart = adjustedActualSizeLeft + roomCenter;
                actualMeasurementEnd = adjustedActualSizeRight + roomCenter;
            } else if (isLeftWidth2Wall) {
                width = boundingInfo.size.depth;
                actualWidth = boundingInfo.actualSize.depth;

                const offset =
                    type === 'cabinet'
                        ? -width / 2 + width + 22.5
                        : -width / 2 + width;

                const actualOffset =
                    type === 'cabinet'
                        ? -actualWidth / 2 + actualWidth + 22.5
                        : 0;

                // Apply lengthDifference adjustment for L-shaped rooms (same logic as createRoomObjects.ts)
                const adjustedBoundingBox = calculateAdjustedBoundingBox(
                    boundingInfo,
                    config,
                    'leftWidth2'
                );
                const {
                    adjustedBoundingInfoLeft,
                    adjustedBoundingInfoRight,
                    adjustedActualSizeLeft,
                    adjustedActualSizeRight,
                } = adjustedBoundingBox;

                // Calculate xPosition using room coordinate system (center = 0)
                if (config.roomWidth && config.scale) {
                    xPosition =
                        adjustedBoundingInfoRight +
                        (config.roomWidth * config.scale) / 2 +
                        35 -
                        offset;
                    actualPostionX =
                        adjustedBoundingInfoRight -
                        (config.roomWidth * config.scale) / 2 +
                        35 +
                        actualOffset;
                } else {
                    xPosition = 0;
                    actualPostionX = 0;
                }

                // Convert cabinet positions from room coordinates to wall coordinates
                const roomCenter = (config.roomWidth * config.scale) / 2;
                measurementStart = adjustedBoundingInfoLeft + roomCenter;
                measurementEnd = adjustedBoundingInfoRight + roomCenter;
                actualMeasurementStart = adjustedActualSizeLeft + roomCenter;
                actualMeasurementEnd = adjustedActualSizeRight + roomCenter;
            }

            const showVerticalLines =
                cabinet.userData.id === cabinetGroup?.userData.id;

            let zPosition = 0;
            if (isLeftWidthWall || isRightDepthWall || isFrontWall) {
                zPosition = 30;
            } else if (isLeftWall) {
                zPosition = 30;
                if (isFrontElevation) {
                    zPosition = -15;
                }
            } else if (isLeftWidth2Wall || isRightWidth2Wall) {
                zPosition = 15;
            }

            // Arrow length must match the labeled size (cabinet_width / height),
            // not the mesh AABB, or print elevations show gaps like 16mm that
            // are only bounding-box noise.
            if (!displayHeightOnWall && Number.isFinite(actualWidth)) {
                width = actualWidth;
            }

            const measurementResult = {
                start: measurementStart,
                end: measurementEnd,
                colour: '#333333',
                label: `${label}`,
                type: 'cabinet' as const,
                id: cabinetId,
                yPosition:
                    typeof yPosition === 'undefined'
                        ? config.wallHeight + 10
                        : yPosition,
                zPosition,
                cabinetId,
                showGuidelines: showVerticalLines,
                priority: 10,
                // Store additional data for visual generation
                xPosition: xPosition, // Calculated position for arrows for alignment
                actualPostionX: actualPostionX, // Computation for real Measurement
                width: width, // Actual width for arrows,
                actualWidth: actualWidth,
                doorWidth: doorThickness,
                actualSize: {
                    start: actualMeasurementStart,
                    end: actualMeasurementEnd,
                    width:
                        isLeftWall ||
                        isRightWall ||
                        isLeftWidthWall ||
                        isRightWidthWall
                            ? boundingInfo.actualSize.depth
                            : boundingInfo.actualSize.width,
                    depth:
                        isLeftWall ||
                        isRightWall ||
                        isLeftWidthWall ||
                        isRightWidthWall
                            ? boundingInfo.actualSize.width
                            : boundingInfo.actualSize.depth,
                    height: boundingInfo.actualSize.height,
                },
                currentDirection,
                cabinet: cabinet,
            };

            if (
                cabinet.userData.isLShapedProduct &&
                !isManualPanel &&
                !cabinet.userData.isPanelProduct &&
                !cabinet.userData.isPanelProductWithLength &&
                !cabinet.userData.isPanelProductWithWidth &&
                !cabinet.userData.isUShapedPanel
            ) {
                const measuredWidthMm =
                    measurementResult.width / (config.scale || 1);
                const legWidth = getLShapeLegWidthBySpan(
                    values,
                    measuredWidthMm
                );
                if (typeof legWidth === 'number') {
                    measurementResult.label = `${legWidth}`;
                }
            }

            return measurementResult;
        } catch (error) {
            return {
                start: 0,
                end: 0,
                colour: '#333333',
                label: '',
                type: 'cabinet' as const,
                id: `cabinet-${index}`,
                yPosition: config.wallHeight + 10,
                zPosition:
                    config.wallRefId === OBJECT_NAMES.BACK_WALL ||
                    config.wallRefId === OBJECT_NAMES.RIGHT_WIDTH_WALL
                        ? 0
                        : config.wallRefId === OBJECT_NAMES.LEFT_WALL ||
                            config.wallRefId === OBJECT_NAMES.LEFT_WIDTH_WALL ||
                            config.wallRefId === OBJECT_NAMES.RIGHT_DEPTH_WALL
                          ? 30
                          : config.wallRefId === OBJECT_NAMES.RIGHT_WIDTH2_WALL
                            ? 30
                            : 0,
                cabinetId: `cabinet-${index}`,
                showGuidelines: false,
                priority: 10,
                xPosition: 0,
                width: 0,
                actualWidth: 0,
                currentDirection: 'front',
            };
        }
    });
};

// --- emptySpaceMeasurementData.ts ---
// Position calculation helpers
export const calculateGapXPosition = (
    currentCabinet: MeasurementData,
    nextCabinet: MeasurementData,
    wallBounds: {
        leftWall: number;
        rightWall: number;
        center: number;
        fullWidth: number;
    },
    wallType: WallType
): number => {
    const {
        isLeftWall,
        isRightWall,
        isLeftWidthWall,
        isRightWidthWall,
        isLeftDepthWall,
        isRightDepthWall,
        isRightWidth2Wall,
        isLeftWidth2Wall,
    } = wallType;

    if (isLeftWall || isLeftWidthWall) {
        return (
            wallBounds.fullWidth -
            (currentCabinet.end + nextCabinet.start) / 2 -
            12.4
        );
    } else if (isRightWidthWall) {
        return (
            wallBounds.fullWidth -
            (currentCabinet.end + nextCabinet.start) / 2 +
            60
        );
    } else if (isRightWall) {
        return (
            wallBounds.fullWidth -
            (currentCabinet.end + nextCabinet.start) / 2 +
            12.4
        );
    } else if (isRightDepthWall) {
        return (
            wallBounds.leftWall +
            (currentCabinet.end + nextCabinet.start) / 2 +
            12
        );
    } else if (isLeftDepthWall) {
        return (
            wallBounds.leftWall +
            (currentCabinet.end + nextCabinet.start) / 2 -
            12
        );
    } else if (isRightWidth2Wall) {
        return (
            wallBounds.fullWidth -
            (currentCabinet.end + nextCabinet.start) / 2 +
            57
        );
    } else if (isLeftWidth2Wall) {
        return (
            wallBounds.leftWall +
            (currentCabinet.end + nextCabinet.start) / 2 +
            12
        );
    } else {
        return (
            wallBounds.fullWidth -
            (currentCabinet.end + nextCabinet.start) / 2 +
            35
        );
    }
};

export const calculateLeftXPosition = (
    firstCabinet: MeasurementData,
    wallBounds: {
        leftWall: number;
        rightWall: number;
        center: number;
        fullWidth: number;
    },
    wallType: WallType
): number => {
    const {
        isLeftWall,
        isRightWall,
        isLeftWidthWall,
        isRightWidthWall,
        isLeftDepthWall,
        isRightDepthWall,
        isRightWidth2Wall,
        isLeftWidth2Wall,
    } = wallType;

    if (isLeftWall) {
        return (wallBounds.fullWidth + firstCabinet.xPosition) / 2 - 25;
    } else if (isLeftWidthWall) {
        return (wallBounds.fullWidth + firstCabinet.xPosition) / 2 - 25;
    } else if (isRightWidthWall) {
        return (wallBounds.fullWidth + firstCabinet.xPosition) / 2 + 25;
    } else if (isRightWall) {
        return (wallBounds.fullWidth + firstCabinet.xPosition) / 2;
    } else if (isRightDepthWall) {
        return (wallBounds.leftWall + firstCabinet.xPosition) / 2 - 10;
    } else if (isLeftDepthWall) {
        return (wallBounds.leftWall + firstCabinet.xPosition) / 2 - 10;
    } else if (isRightWidth2Wall) {
        return (wallBounds.fullWidth + firstCabinet.xPosition) / 2 + 8;
    } else if (isLeftWidth2Wall) {
        return (wallBounds.leftWall + firstCabinet.start) / 2 + 10;
    } else {
        return (wallBounds.fullWidth + firstCabinet.xPosition) / 2;
    }
};

export const calculateRightXPosition = (
    lastCabinet: MeasurementData,
    wallBounds: {
        leftWall: number;
        rightWall: number;
        center: number;
        fullWidth: number;
    },
    wallType: WallType
): number => {
    const {
        isLeftWall,
        isRightWall,
        isLeftWidthWall,
        isRightWidthWall,
        isLeftDepthWall,
        isRightDepthWall,
        isRightWidth2Wall,
        isLeftWidth2Wall,
    } = wallType;

    if (isLeftWall) {
        return wallBounds.center - lastCabinet.end / 2 - 5;
    } else if (isLeftWidthWall) {
        return wallBounds.center - lastCabinet.end / 2 - 5;
    } else if (isRightWidthWall) {
        return wallBounds.center - lastCabinet.end / 2 + 40;
    } else if (isRightWall) {
        return wallBounds.center - lastCabinet.end / 2 + 20;
    } else if (isRightDepthWall) {
        return wallBounds.center + lastCabinet.end / 2 + 5;
    } else if (isLeftDepthWall) {
        return wallBounds.center + lastCabinet.end / 2 + 15;
    } else if (isRightWidth2Wall) {
        return wallBounds.center - lastCabinet.end / 2 + 40;
    } else if (isLeftWidth2Wall) {
        return wallBounds.center + lastCabinet.end / 2 + 5;
    } else {
        return wallBounds.center - lastCabinet.end / 2 + 18;
    }
};

// Z-position calculation helper
export const calculateZPosition = (
    wallType: WallType,
    config: MeasurementConfig
): number => {
    const {
        isLeftWall,
        isLeftWidthWall,
        isRightDepthWall,
        isRightWidth2Wall,
        isLeftWidth2Wall,
    } = wallType;

    if (isLeftWall || isLeftWidthWall || isRightDepthWall) {
        return 30;
    } else if (config.wallRefId === OBJECT_NAMES.FRONT_WALL) {
        return 30;
    } else if (isRightWidth2Wall || isLeftWidth2Wall) {
        return 15;
    } else {
        return 0;
    }
};

/**
 * Wall-axis span from product dimensions (actualSize), not the mesh AABB.
 * Print elevations must use this so cabinet labels and empty-space gaps add
 * up to the room width.
 * @param {MeasurementData} data - Cabinet measurement row
 * @return {{start: number, end: number, width: number}} Normalized span in scene units
 */
export const getCabinetProductSpan = (
    data: MeasurementData
): {start: number; end: number; width: number} => {
    const extents = getActualMeasurementExtents(data);
    const start = Math.min(extents.start, extents.end);
    const end = Math.max(extents.start, extents.end);
    return {start, end, width: end - start};
};

/**
 * Mesh AABB centers of a stacked base+upper can differ by a few mm, so the
 * union of two 900s becomes 908 and the leftover wall becomes 7092 instead of
 * the plan-view 900+7100. Treat that skew as the same slot.
 */
export const STACKED_SPAN_SKEW_MM = 20;

export const getOccupiedProductSpan = (
    cabinets: MeasurementData[],
    scale: number
): {start: number; end: number; width: number} => {
    if (!cabinets.length) {
        return {start: 0, end: 0, width: 0};
    }

    const spans = cabinets
        .map(getCabinetProductSpan)
        .sort((left, right) => left.start - right.start);
    const merged: {start: number; end: number; width: number}[] = [];

    spans.forEach((span) => {
        const last = merged.at(-1);
        if (!last) {
            merged.push({...span});
            return;
        }

        const overlaps = span.start <= last.end + 1e-4;
        const unionStart = Math.min(last.start, span.start);
        const unionEnd = Math.max(last.end, span.end);
        const maxWidth = Math.max(last.width, span.width);
        const unionWidth = unionEnd - unionStart;
        const skewMm = scale
            ? (unionWidth - maxWidth) / scale
            : unionWidth - maxWidth;

        if (overlaps && skewMm <= STACKED_SPAN_SKEW_MM) {
            last.start = unionStart;
            last.width = maxWidth;
            last.end = unionStart + maxWidth;
            return;
        }

        if (overlaps) {
            last.start = unionStart;
            last.end = unionEnd;
            last.width = unionEnd - unionStart;
            return;
        }

        merged.push({...span});
    });

    const start = merged[0].start;
    const end = merged[merged.length - 1].end;
    return {start, end, width: end - start};
};

export const generateEmptySpaceMeasurementData = (
    cabinetGroupList: THREE.Group<THREE.Object3DEventMap>[],
    config: MeasurementConfig,
    measurementDataRef: MeasurementData[] = []
): MeasurementData[] => {
    if (!config.roomWidth || !config.scale) {
        return [];
    }

    const measurements: MeasurementData[] = [];

    // Handle different wall types - separate logic for left and right walls
    const wallType = getWallType(config.wallRefId);
    const {
        isLeftWall,
        isFrontWall,
        isRightWall,
        isLeftWidthWall,
        isRightWidthWall,
        isLeftDepthWall,
        isRightDepthWall,
        isRightWidth2Wall,
        isLeftWidth2Wall,
        isBackWall,
    } = wallType;
    // Get the appropriate room dimension based on wall type
    const wallBounds = getWallBoundaries(config);
    const isFrontElevation =
        config.isForPrinting && config.previewPosition === 'FRONT';
    const isLeftElevation =
        config.isForPrinting && config.previewPosition === 'RIGHT_LEVELED';
    const isRightElevation =
        config.isForPrinting && config.previewPosition === 'LEFT_LEVELED';
    const isPlanView = config.isForPrinting && config.previewPosition === 'TOP';

    const displayHeightOnWall =
        (isFrontElevation && (isLeftWall || isLeftWidthWall)) ||
        (isLeftElevation && (isFrontWall || isLeftDepthWall)) ||
        (isRightElevation && (isBackWall || isRightWidthWall));

    // If no cabinets, measure the entire wall
    if (cabinetGroupList.length === 0) {
        let zPosition = 0;
        if (
            isLeftWall ||
            isLeftWidthWall ||
            isRightDepthWall ||
            config.wallRefId === OBJECT_NAMES.FRONT_WALL ||
            isRightWidth2Wall ||
            isLeftWidth2Wall
        ) {
            zPosition = 30;
        }

        const wallSize = 30;
        let startMeasurement = wallBounds.leftWall; // Start from left wall (0)
        let endMeasurement = wallBounds.rightWall; // End at right wall (roomWidth * scale)
        let width = wallBounds.fullWidth;
        if (displayHeightOnWall) {
            startMeasurement = 0; // bottom
            endMeasurement = config.wallHeight; // top
            width = config.wallHeight * config.scale;
        } else if (isPlanView && (isLeftWidth2Wall || isRightWidth2Wall)) {
            width = wallBounds.fullWidth - wallSize;
        }

        let xPosition = wallBounds.center;
        if (isLeftDepthWall || isRightWidthWall || isRightWall) {
            xPosition += 30;
        } else if (isRightWidth2Wall) {
            xPosition += isPlanView ? -10 + wallSize / 2 : 20;
        } else if (isLeftWidth2Wall) {
            xPosition += isPlanView ? 30 - wallSize / 2 : 0;
        }

        let yPosition = config.wallHeight + 10;
        if (displayHeightOnWall) {
            yPosition = config.wallHeight / 2;
            xPosition = 0;
        }

        measurements.push({
            start: startMeasurement,
            end: endMeasurement,
            colour: '#A9A9A9', // Gray for empty spaces
            label: formatMmLabelFromScaled(wallBounds.fullWidth, config.scale),
            type: 'emptySpace' as const,
            id: 'wall-full',
            yPosition,
            zPosition,
            showGuidelines: false,
            priority: 5, // Medium priority for empty spaces
            xPosition, // Center position for arrows
            width,
        });
        return measurements;
    }

    // Get cabinet measurement data (already in room coordinates)
    const cabinetMeasurementData = generateCabinetMeasurementData(
        cabinetGroupList,
        config,
        'emptySpace'
    );

    // If any cabinet label has a decimal, use decimal precision for empty
    // space labels (right, left, between cabinets, floor/height)
    const hasDecimal = hasDecimalFromLabels(
        cabinetMeasurementData.map((cab) => cab.label)
    );
    const formatEmptyLabel = (scaledValue: number, scale: number): string =>
        formatMmLabel(scaledValue, scale, hasDecimal);

    // Sort by product-size span (cabinet_width), not mesh AABB. Bounding-box
    // start/end is 16mm short of the labeled 900 and prints a phantom gap.
    const sortedCabinets: MeasurementData[] = [...cabinetMeasurementData].sort(
        (a, b) =>
            getCabinetProductSpan(a).start - getCabinetProductSpan(b).start
    );

    // Generate gap measurements
    if (sortedCabinets.length > 0) {
        // Get wall boundaries using the helper function
        const wallBounds = getWallBoundaries(config);

        // Leftmost cabinet = smallest start; rightmost cabinet = largest end
        // (which is not necessarily the last one in start order).
        const firstCabinet = sortedCabinets[0];
        const lastCabinet = sortedCabinets.reduce((rightmost, cab) =>
            getCabinetProductSpan(cab).end >
            getCabinetProductSpan(rightmost).end
                ? cab
                : rightmost
        );
        const firstSpan = getCabinetProductSpan(firstCabinet);

        // Left gap matches DistanceEditor (blue arrows). Right gap is the
        // remainder so labeled cabinet widths + gaps equal the room width.
        const roomConfig = toRoomConfig(config);
        const wallRefId = config.wallRefId as WallRefId;

        const firstGaps =
            firstCabinet.cabinet && roomConfig
                ? getWallGaps(firstCabinet.cabinet, roomConfig, wallRefId, true)
                : {leftGap: 0, rightGap: 0};
        const leftGapWidth = firstGaps.leftGap;

        const minGapWorld = 0;
        // Gaps between cabinets. Track the furthest right edge seen so far so
        // overlapping cabinets (e.g. a base under a wider upper) don't inflate
        // the following gap — the empty space starts at the cluster's max end.
        // Height elevations use start/end as Y (cabinet bottom/top). Treating
        // those as horizontal spans duplicates the splashback (e.g. 680) and
        // parks it at wallHeight+10 — the arrow that prints above the 2800 wall.
        if (!displayHeightOnWall) {
            let coveredEnd = firstSpan.end;
            for (let i = 0; i < sortedCabinets.length - 1; i++) {
                const currentCabinet = sortedCabinets.at(i);
                const nextCabinet = sortedCabinets[i + 1];

                // Check if both current and next cabinet exist
                if (!currentCabinet || !nextCabinet) {
                    continue;
                }

                coveredEnd = Math.max(
                    coveredEnd,
                    getCabinetProductSpan(currentCabinet).end
                );

                const nextSpan = getCabinetProductSpan(nextCabinet);
                const gapWidth = nextSpan.start - coveredEnd;
                if (gapWidth > minGapWorld) {
                    const xPosition = calculateGapXPosition(
                        {
                            ...currentCabinet,
                            start: coveredEnd,
                            end: coveredEnd,
                        },
                        {
                            ...nextCabinet,
                            start: nextSpan.start,
                            end: nextSpan.end,
                        },
                        wallBounds,
                        wallType
                    );

                    measurements.push({
                        start: coveredEnd,
                        end: nextSpan.start,
                        colour: '#A9A9A9',
                        label: formatEmptyLabel(gapWidth, config.scale),
                        type: 'emptySpace' as const,
                        id: `gap-${currentCabinet.id}-${nextCabinet.id}`,
                        yPosition: config.wallHeight + 10,
                        zPosition: calculateZPosition(wallType, config),
                        showGuidelines: false,
                        priority: 5,
                        xPosition: xPosition,
                        width: gapWidth,
                        actualWidth: gapWidth,
                    });
                }
            }
        }

        // Same minimum as between-cabin gaps — avoids corner "0" labels from
        // sub-threshold gaps that round to 0 in formatMmLabel.
        if (leftGapWidth > minGapWorld && !displayHeightOnWall) {
            const leftXPosition = calculateLeftXPosition(
                firstCabinet,
                wallBounds,
                wallType
            );

            measurements.push({
                start: wallBounds.leftWall,
                end: firstSpan.start,
                colour: '#A9A9A9',
                label: formatEmptyLabel(leftGapWidth, config.scale),
                type: 'emptySpace' as const,
                id: 'gap-left-wall',
                yPosition: config.wallHeight + 10,
                zPosition: calculateZPosition(wallType, config),
                showGuidelines: false,
                priority: 5,
                xPosition: leftXPosition,
                width: leftGapWidth,
            });
        }

        // Remainder after left gap + occupied product span so stacked 900s
        // stay 900+7100 like plan view, not 908+7092 from AABB-center skew.
        const occupiedWidth = getOccupiedProductSpan(
            sortedCabinets,
            config.scale
        ).width;
        const rightGapWidth =
            wallBounds.fullWidth - leftGapWidth - occupiedWidth;

        // Add right gap measurement
        if (rightGapWidth > minGapWorld && !displayHeightOnWall) {
            const rightXPosition = calculateRightXPosition(
                lastCabinet,
                wallBounds,
                wallType
            );

            measurements.push({
                start: wallBounds.fullWidth,
                end: wallBounds.fullWidth,
                colour: '#A9A9A9',
                label: formatEmptyLabel(rightGapWidth, config.scale),
                type: 'emptySpace' as const,
                id: 'gap-right-wall',
                yPosition: config.wallHeight + 10,
                zPosition: calculateZPosition(wallType, config),
                showGuidelines: false,
                priority: 5,
                xPosition: rightXPosition,
                width: rightGapWidth,
                actualWidth: rightGapWidth,
            });
        }

        // Elevation heights: cabinets that share a labeled size and vertical
        // position are already one row. Each row gets the distance from the
        // floor to its bottom and from its top to the ceiling, so every
        // column sums to the wall height instead of sharing one stack.
        if (displayHeightOnWall) {
            const mmFromScaled = (scaled: number): number =>
                Math.round(scaled / config.scale);

            // One row per labeled size and vertical position. Side-by-side
            // cabinets that match share that label, then one floor distance
            // and one ceiling distance.
            const heightRows: MeasurementData[] = [];
            measurementDataRef.forEach((data) => {
                if (
                    !data.cabinet ||
                    !Number.isFinite(data.start) ||
                    !Number.isFinite(data.end)
                ) {
                    return;
                }
                const alreadyGrouped = heightRows.some((existing) =>
                    elevationHeightsMatch(existing, data)
                );
                if (!alreadyGrouped) {
                    heightRows.push(data);
                }
            });

            const ceilingY = config.wallHeight - 20;
            const wallHeightMm = mmFromScaled(ceilingY);
            const rowMm = (row: MeasurementData): number => {
                const fromLabel = Number(row.label);
                return Number.isFinite(fromLabel)
                    ? fromLabel
                    : mmFromScaled(Math.abs(row.end - row.start));
            };

            const pushHeightEmpty = ({
                start,
                end,
                label,
                id,
                cabinetId,
                zPosition,
                priority,
            }: {
                start: number;
                end: number;
                label: string;
                id: string;
                cabinetId?: string;
                zPosition: number;
                priority?: number;
            }) => {
                const width = end - start;
                measurements.push({
                    start,
                    end,
                    colour: '#A9A9A9',
                    label,
                    type: 'emptySpace' as const,
                    id,
                    cabinetId,
                    yPosition: (start + end) / 2,
                    zPosition,
                    showGuidelines: false,
                    priority,
                    xPosition: 0,
                    width,
                    actualWidth: width,
                });
            };

            heightRows.forEach((row) => {
                const bottomCabinetData = row.cabinet?.userData as
                    | {
                          isUpperProduct?: boolean;
                          sitOnFloor?: boolean;
                      }
                    | undefined;
                const isToeKickGap =
                    !bottomCabinetData?.isUpperProduct &&
                    !bottomCabinetData?.sitOnFloor;

                // Floor to this group's bottom. Subtract 5mm so the printed
                // gap matches the elevation (scene placement sits 5mm high).
                const bottomGapEnd = getHeightFloorGapEnd(
                    row.start,
                    config.scale || 1
                );
                const bottomLabel = formatEmptyLabel(
                    bottomGapEnd,
                    config.scale || 1
                );
                const bottomGapMm = Number(bottomLabel) || 0;
                if (bottomGapMm > 0) {
                    pushHeightEmpty({
                        start: 0,
                        end: bottomGapEnd,
                        label: bottomLabel,
                        id: isToeKickGap
                            ? `gap-toe-cabinet-${row.cabinetId}`
                            : `gap-floor-${row.cabinetId}`,
                        cabinetId: row.cabinetId,
                        zPosition: row.zPosition,
                        priority: row.priority,
                    });
                }

                // Leftover so floor + labeled height + ceiling equals the wall.
                const topGapMm = wallHeightMm - bottomGapMm - rowMm(row);
                if (topGapMm > 0) {
                    const topGapScaled = topGapMm * (config.scale || 1);
                    measurements.push({
                        start: row.end,
                        end: ceilingY,
                        colour: '#A9A9A9',
                        label: `${topGapMm}`,
                        type: 'emptySpace' as const,
                        id: `gap-ceiling-${row.cabinetId}`,
                        cabinetId: row.cabinetId,
                        yPosition: (row.end + ceilingY) / 2,
                        zPosition: row.zPosition,
                        showGuidelines: false,
                        priority: row.priority,
                        xPosition: 0,
                        width: topGapScaled,
                        actualWidth: topGapScaled,
                    });
                }
            });
        }
    }

    return measurements;
};
